import React from "react";
import type { ChangeEvent } from "react";
import classnames from "classnames";
import {
  Button,
  Row,
  Col,
  Label,
  FormGroup,
  InputGroup,
  Input,
} from "reactstrap";

type AddPropertyViewProps = {
  property: {
    name: string;
    description: string;
    type: string;
    order_listing: string;
    order_product: string;
  };
  validation: {
    property: {
      name: boolean;
      description: boolean;
      type: boolean;
      order_listing: boolean;
      order_product: boolean;
    };
  };
  showChecked: boolean;
  boxChecked: boolean;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onChangeCheckboxShow: (e: ChangeEvent<HTMLInputElement>) => void;
  onChangeCheckboxBox: (e: ChangeEvent<HTMLInputElement>) => void;
  onClickAdd: () => void;
  /** SCRUM-1819: the soft-deleted property a 409 named; `null` while there is none. */
  deletedHolder: { msg: string; value: string } | null;
  onRestore: () => void;
  onStartFresh: () => void;
};

export default function AddPropertyView({
  property,
  validation,
  showChecked,
  boxChecked,
  onChange,
  onChangeCheckboxShow,
  onChangeCheckboxBox,
  onClickAdd,
  deletedHolder,
  onRestore,
  onStartFresh,
}: AddPropertyViewProps) {
  return (
    <>
      <Row style={{ display: "flex", position: "relative" }}>
        <Col>
          {/* PROPERTY NAME */}
          <FormGroup className="row">
            <Label className="form-control-label" md="2">
              Property Name
            </Label>
            <Col md="7">
              <InputGroup className={classnames("input-group-merge")}>
                <Input
                  placeholder="Add Property Name "
                  name="name"
                  type="text"
                  value={property.name}
                  onChange={onChange}
                  invalid={!validation.property.name}
                />
              </InputGroup>
              {!validation.property.name && (
                <InputGroup className="invalid-feedback">
                  Required Property Name.
                </InputGroup>
              )}
            </Col>
          </FormGroup>

          {/* DESCRIPTION */}
          <FormGroup className="row">
            <Label className="form-control-label" md="2">
              Property Description
            </Label>
            <Col md="7">
              <InputGroup className={classnames("input-group-merge")}>
                <Input
                  placeholder="Add Property Description "
                  name="description"
                  type="text"
                  value={property.description}
                  onChange={onChange}
                  invalid={!validation.property.description}
                />
              </InputGroup>
              {!validation.property.description && (
                <InputGroup className="invalid-feedback">
                  Required Property Description.
                </InputGroup>
              )}
            </Col>
          </FormGroup>

          {/* TYPE */}
          <FormGroup className="row">
            <Label className="form-control-label" md="2">
              Property Type
            </Label>
            <Col md="7">
              <InputGroup className={classnames("input-group-merge")}>
                <Input
                  placeholder="Add Property Type "
                  name="type"
                  type="text"
                  value={property.type}
                  onChange={onChange}
                  invalid={!validation.property.type}
                />
              </InputGroup>
              {!validation.property.type && (
                <InputGroup className="invalid-feedback">
                  Required Property Type.
                </InputGroup>
              )}
            </Col>
          </FormGroup>

          {/* ORDER LISTING */}
          <FormGroup className="row">
            <Label className="form-control-label" md="2">
              Order Listing
            </Label>
            <Col md="7">
              <InputGroup className={classnames("input-group-merge")}>
                <Input
                  placeholder="Add Order Listing "
                  name="order_listing"
                  type="text"
                  value={property.order_listing}
                  onChange={onChange}
                  invalid={!validation.property.order_listing}
                />
              </InputGroup>
              {!validation.property.order_listing && (
                <InputGroup className="invalid-feedback">
                  Required Property Type.
                </InputGroup>
              )}
            </Col>
          </FormGroup>

          {/* ORDER PRODUCT */}
          <FormGroup className="row">
            <Label className="form-control-label" md="2">
              Order Product
            </Label>
            <Col md="7">
              <InputGroup className={classnames("input-group-merge")}>
                <Input
                  placeholder="Add Order Product "
                  name="order_product"
                  type="text"
                  value={property.order_product}
                  onChange={onChange}
                  invalid={!validation.property.order_product}
                />
              </InputGroup>
              {!validation.property.order_product && (
                <InputGroup className="invalid-feedback">
                  Required Property Type.
                </InputGroup>
              )}
            </Col>
          </FormGroup>

          {/* Show In Listing */}
          <FormGroup className="row">
            <Label className="form-control-label" md="2">
              Show In Listing
            </Label>
            <Col md="7">
              <InputGroup className={classnames("input-group-merge")}>
                <label className="custom-toggle custom-toggle-danger mr-1">
                  <input
                    checked={showChecked}
                    onChange={onChangeCheckboxShow}
                    name="show"
                    type="checkbox"
                    aria-label="Show in listing"
                  />
                  <span
                    className="custom-toggle-slider rounded-circle"
                    data-label-off="No"
                    data-label-on="Yes"
                  />
                </label>
              </InputGroup>
            </Col>
          </FormGroup>

          {/* Show In Details Box */}
          <FormGroup className="row">
            <Label className="form-control-label" md="2">
              Show In Details Box
            </Label>
            <Col md="7">
              <InputGroup className={classnames("input-group-merge")}>
                <label className="custom-toggle custom-toggle-danger mr-1">
                  <input
                    checked={boxChecked}
                    onChange={onChangeCheckboxBox}
                    name="box"
                    type="checkbox"
                    aria-label="Show in details box"
                  />
                  <span
                    className="custom-toggle-slider rounded-circle"
                    data-label-off="No"
                    data-label-on="Yes"
                  />
                </label>
              </InputGroup>
            </Col>
          </FormGroup>

          {/*
            SCRUM-1819: a 409 from POST /api/property/add names the
            soft-deleted property that still holds this name. Two ways out,
            each saying what happens to the old property's links.
          */}
          {deletedHolder && (
            <div className="alert alert-warning" role="alert">
              <p className="mb-2">
                {deletedHolder.msg}
                {deletedHolder.value ? (
                  <>
                    {" "}
                    (<strong>{deletedHolder.value}</strong>)
                  </>
                ) : null}
                .
              </p>
              <p className="mb-2">
                <Button
                  type="button"
                  size="sm"
                  color="warning"
                  className="mr-2"
                  onClick={onRestore}
                >
                  Restore
                </Button>
                Restore brings the old property back, with its labels and
                settings. This new property is not created.
              </p>
              <p className="mb-0">
                <Button
                  type="button"
                  size="sm"
                  outline
                  color="warning"
                  className="mr-2"
                  onClick={onStartFresh}
                >
                  Start fresh
                </Button>
                Start fresh creates a new property. Product values and
                subcategory links of the old property stay with the deleted
                record and do not move to the new one.
              </p>
            </div>
          )}

          {/* ADD BUTTON */}
          <Col>
            <FormGroup style={{ right: 0, position: "absolute" }} className="row">
              <Button
                className="btn btn-outline-primary mr-1 float-right"
                color="primary"
                type="button"
                onClick={onClickAdd}
              >
                Add Property
              </Button>
            </FormGroup>
          </Col>
        </Col>
      </Row>
    </>
  );
}
